import React, { useCallback, useEffect, useState } from "react";
import './EcoProSDTicket.css'
import EcoProButton from "../../../shared/button/button";
import { DisplayDate } from "../../../../utils/DisplayDate";
import { BROTHER_SERVICE_DESK_BASE_URL } from "../../../../constants/AppConstants";
import EcoProOverlayModal from "../../../shared/modal/OverlayModal/EcoProOverlayModal";
import { useTranslation } from "react-i18next";
import SDTicketModalContent from "./SDTicketModalContent/SDTicketModalContent";
import { useDispatch, useSelector } from "react-redux";
import { FetchDeviceAndUsageDetails, FetchSubscriptionDetails, ResetDeviceAndUsageDetails, ResetSubscriptionDetails } from "../../../../actions/CSAgentPortal/CustomerDashboard";
import { FETCH_DEVICE_AND_USAGE_DETAILS_INIT } from "../../../../constants/ActionTypes/CSAgentPortal/CustomerDashboard";
import Loader from "../../../shared/loader/Loader";

const EcoProSDTicket = (props) => {
    const { t } = useTranslation("CSAgenttranslation");
    const dispatch = useDispatch();
    const { customerInfo } = props;
    const NOT_AVAILABLE = 'NA';
    const DeviceAndDeviceUsageDetails = useSelector((state) => state.CSAGENT.DeviceAndDeviceUsageDetails);
    const SubscriptionInfo = useSelector(state => state.CSAGENT.SubscriptionDetails);
    const { userId, businessPartnerId = null } = customerInfo || {};

    const modalPropsInitialState = {
        modal_title: "",
        modal_sub_title: "",
        modal_content: "",
        cancel_btn_text: t("MODAL_BUTTON_TXT.CANCEL"),
        confirm_btn_text: t("MODAL_BUTTON_TXT.CREATE"),
        toggleModal: "",
        customClassName: "sd-ticket-container",
        confirm_btn_onClick: "",
        type: "normal",
        modal_size: ""
    }
    const formValuesInitialState = {
        ticketSerialNumber: [],
        ticketSummary: '',
        ticketC4CReference: '',
        ticketCategory: ''
    }
    const [formValues, setFormValues] = useState(formValuesInitialState);
    const [errors, setErrors] = useState({});
    const [modalState, setModalState] = useState(false);
    const [modalProps, setModalProps] = useState(modalPropsInitialState);

    const toggleModal = () => {
        setModalState(false);
        setErrors({});
        setModalProps(modalPropsInitialState);
        setFormValues(formValuesInitialState);
    }
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues(prevValues => ({
            ...prevValues,
            [name]: value
        }));
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: ""
        }));
    };
    const handleDates = (date) => {
        return DisplayDate(date, { day: '2-digit', month: 'long', year: 'numeric' });
    }
    const handleServiceCancellationData = (serviceCancellationDates, delayedCancellationRequestDate, delayedCancellationDate) => {
        let result;
        if (!serviceCancellationDates && !delayedCancellationDate) {
            result = NOT_AVAILABLE
        } else if (serviceCancellationDates) {
            result = `  
                        ** Cancelled Date: ${handleDates(serviceCancellationDates) || NOT_AVAILABLE}
                    `
        } else if (delayedCancellationDate) {
            result = `  
                        ** Cancellation Requested Date: ${handleDates(delayedCancellationRequestDate) || NOT_AVAILABLE}
                        ** Cancellation Date: ${handleDates(delayedCancellationDate) || NOT_AVAILABLE}
                    `
        }

        return result;
    }

    /**
     * Method to construct summary for SD tickets
     * @param {string} ticketCategory 
     * @returns string
     */
    const constructTicketSummary = useCallback((ticketCategory) => {
        return encodeURIComponent(`${ticketCategory} - ${userId}`);
    }, [userId]);

    /**
     * Method to construct description for SD tickets
     * @param {string} description 
     * @param {string} SAPC4CRef 
     * @returns string
     */
    const constructTicketDescription = useCallback((description, SAPC4CRef, serialNumber) => {
        const selectedSubscriptionData = SubscriptionInfo?.subscriptionInfo?.subscriptionDetails?.filter(
            (subscription) => { return (subscription.serialNumber === serialNumber) })[0]

        const selectedDeviceDetails = DeviceAndDeviceUsageDetails?.deviceInfo?.activeDevices?.filter(
            (deviceData) => { return (deviceData.serialNumber === serialNumber) })[0]

        const txt = `
            ${description} \n
            * Customer ID: ${userId}
            * Business Partner Id: ${businessPartnerId}
            * SAP C4C Reference: ${SAPC4CRef || NOT_AVAILABLE}
            * Device ID: ${selectedSubscriptionData?.deviceId || NOT_AVAILABLE}
            * Device Model: ${selectedDeviceDetails?.modelNumber || NOT_AVAILABLE}
            * Device Serial Number: ${selectedSubscriptionData?.serialNumber || serialNumber}
            * Subscription Status: ${selectedSubscriptionData?.subscriptionStatus || NOT_AVAILABLE}
            * Subscription Activated Date: ${handleDates(selectedSubscriptionData?.activatedDate) || NOT_AVAILABLE}
            * Subscription Cancellation Dates: ${handleServiceCancellationData(selectedSubscriptionData?.cancelledDate, selectedSubscriptionData?.delayedCancellationRequestDate, selectedSubscriptionData?.delayedCancellationDate)}
        `;

        // Encode the string
        return encodeURIComponent(txt);

        // eslint-disable-next-line
    }, [
        DeviceAndDeviceUsageDetails?.deviceInfo?.activeDevice,
        SubscriptionInfo?.subscriptionInfo?.subscriptionDetails,
        businessPartnerId,
        userId]);

    const handleCreateTicket = useCallback((formValues) => {
        const ticketDeviceSerialNumber = formValues?.ticketDeviceSerialNumber;
        const ticketCategory = formValues?.ticketCategory;
        const description = formValues.ticketSummary;
        const SAPC4CRef = formValues.ticketC4CReference;

        let validationErrors = {};
        if (!ticketCategory) {
            validationErrors.ticketCategory = t('SD_TICKET.MODAL.ERRORS.TICKET_CATEGORY');
        }
        if (!description) {
            validationErrors.ticketSummary = t('SD_TICKET.MODAL.ERRORS.TICKET_DESCRIPTION');
        }

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
        } else {
            const ticketSummary = constructTicketSummary(ticketCategory);
            const ticketDescription = constructTicketDescription(description, SAPC4CRef, ticketDeviceSerialNumber);
            const baseUrl = BROTHER_SERVICE_DESK_BASE_URL;
            const fullUrl = `${baseUrl}?summary=${ticketSummary}&description=${ticketDescription}`;
            window.open(fullUrl, "_blank");
        }
    }, [constructTicketDescription, constructTicketSummary, t]);

    useEffect(() => {
        formValues.ticketSerialNumber = (SubscriptionInfo.success)
            ? SubscriptionInfo?.subscriptionInfo?.subscriptionDetails?.map(obj => obj?.serialNumber)
            : [];

        const modalContent = (DeviceAndDeviceUsageDetails.loading || SubscriptionInfo.loading)
            ? <Loader />
            : <SDTicketModalContent handleChange={handleChange} values={formValues} errors={errors} />

        const displayConfirmBtn = (DeviceAndDeviceUsageDetails.loading || SubscriptionInfo.loading)
            ? false : true;

        setModalProps({
            modal_title: t('SD_TICKET.MODAL.TITLE'),
            modal_content: modalContent,
            toggleModal: toggleModal,
            customClassName: "sd-ticket-container",
            confirm_btn_onClick: () => handleCreateTicket(formValues),
            cancel_btn_text: t("MODAL_BUTTON_TXT.CANCEL"),
            confirm_btn_text: (displayConfirmBtn) ? t("MODAL_BUTTON_TXT.CREATE") : null,
            modal_size: 'lg'
        });
        // eslint-disable-next-line
    }, [formValues, modalState, errors, handleCreateTicket, t, DeviceAndDeviceUsageDetails, SubscriptionInfo]);

    const handleSDTicketModal = () => {
        // reset device and subscription details
        dispatch(ResetSubscriptionDetails());
        dispatch(ResetDeviceAndUsageDetails());

        setModalState(true);
        dispatch(FetchDeviceAndUsageDetails(userId, FETCH_DEVICE_AND_USAGE_DETAILS_INIT));
        dispatch(FetchSubscriptionDetails(userId));
    }

    return (
        <>
            <EcoProButton btnID="SDticket-btn" cname="default create-ticket-btn" onBtnClick={() => handleSDTicketModal()}>
                {t('SD_TICKET.CREATE_TICKET_BTN_TXT')}
            </EcoProButton>
            <EcoProOverlayModal {...modalProps} modal_state={modalState}></EcoProOverlayModal>
        </>
    );
}

export default EcoProSDTicket;